
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useTranslate,
  IResourceComponentsProps,
  useApiUrl,
  HttpError,
  useTable,
  CrudFilters,
  LogicalFilter,
  getDefaultFilter,
} from '@pankod/refine-core';
import {
  List,
  Table,
  TextField,
  DateField,
  EditButton,
  CreateButton,
  Button,
  Space,
  message,
} from '@pankod/refine-antd';
import { ConveyanceStatus } from 'types/ownership/conveyanceStatus';
import { ConveyanceType } from 'types/ownership/conveyanceType';
import { Attachment } from 'interfaces/ownership/attachment';
import { EditOutlined, TableOutlined, ReloadOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { getColumnSearchProps } from 'components/data/customFunc';
import type { Breakpoint } from 'antd/es/_util/responsiveObserve';

interface Conveyance {
  id: number;
  ownership_id: number;
  grant_to: number;
  status: ConveyanceStatus;
  type: ConveyanceType;
  title: string;
  description: string | null;
  amount: number;
  interval: number;
  auto_renew?: boolean;
  start: Date;
  end: Date | null;
  note?: string | null;
  updated_by: number;
  created_by: number;
  created_at: Date | null;
  updated_at: Date | null;
  deleted_at: Date | null;
  owner_name: string | null;
  grant_to_name: string | null;
  attachments: Attachment[];
  [key: string]: any;
}

export const ConveyanceList: React.FC<IResourceComponentsProps> = () => {
  const t = useTranslate();
  const apiUrl = useApiUrl();
  const [dataSource, setDataSource] = useState<Conveyance[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const { tableQueryResult, filters, setFilters } = useTable<
    Conveyance,
    HttpError
  >({
    resource: 'conveyance',
    initialSorter: [
      {
        field: 'id',
        order: 'desc',
      },
    ],
    initialPageSize: pageSize,
  });

  const setData = useCallback(async () => {
    const tableData = tableQueryResult?.data?.data;
    if (!tableData) return;

    const sortedData = [...tableData].sort((a, b) => b.id - a.id);

    const mappedData = sortedData.map((c: Conveyance) => {
      let attachmentPaths: string[] = [];
      if (c.attachments && Array.isArray(c.attachments)) {
        attachmentPaths = c.attachments
          .filter((attachment: Attachment) => {
            if (!attachment?.path) return false;
            const extension = attachment.path.split('.').pop()?.toLowerCase();
            return (
              !!extension && ['jpg', 'jpeg', 'png', 'gif', 'pdf','docx'].includes(extension)
            );
          })
          .map((attachment: Attachment) => {
            return attachment.path!.startsWith('http')
              ? attachment.path!
              : `${process.env.REACT_APP_SERVER}${attachment.path}`;
          });
      }
      return { ...c, attachmentPaths };
    });
    setDataSource(mappedData);
  }, [tableQueryResult.data]);

  const filteredDataSource = useMemo(() => {
    if (!filters?.length) return dataSource;

    return dataSource.filter((item) =>
      filters.every((filter) => {
        if ('field' in filter && filter.value && 'operator' in filter) {
          const itemValue = item[filter.field]?.toString().toLowerCase();
          // Gérer les filtres multiples pour status et type
          if (filter.field === 'status' || filter.field === 'type') {
            const filterValues = Array.isArray(filter.value)
              ? filter.value.map((v: string) => v.toLowerCase())
              : [filter.value.toString().toLowerCase()];
            return filterValues.includes(itemValue);
          }
          // Gérer les filtres de recherche pour title, owner_name, grant_to_name
          const filterValue = filter.value.toString().toLowerCase();
          return itemValue?.includes(filterValue);
        }
        return true;
      }),
    );
  }, [dataSource, filters]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setData();
    }, 0);
    return () => clearTimeout(timer);
  }, [setData]);

  useEffect(() => {
    tableQueryResult.refetch();
  }, [currentPage, tableQueryResult]);

  const handleResetFilters = useCallback(() => {
    setFilters([], 'replace');
    setCurrentPage(1);
    tableQueryResult.refetch();
    message.success(
      t('messages.resetFilters', {
        ns: 'conveyances',
        // defaultValue: 'Filtres réinitialisés',
      }),
    );
  }, [setFilters, tableQueryResult, t]);

  // Options de filtre pour status
  const statusFilters = useMemo(
    () =>
      Object.values(ConveyanceStatus).map((status) => ({
        text: t(`conveyances.status.${status}`, status),
        value: status,
      })),
    [t],
  );

  // Options de filtre pour type
  const typeFilters = useMemo(
    () =>
      Object.values(ConveyanceType).map((type) => ({
        text: t(`conveyances.type.${type}`, type),
        value: type,
      })),
    [t],
  );

  const columns = [
    {
      dataIndex: 'index',
      key: 'index',
      title: t('conveyances.fields.id', 'ID'),
      render: (_: any, __: Conveyance, index: number) => (
        <TextField value={(currentPage - 1) * pageSize + index + 1} />
      ),
      responsive: ['md'] as Breakpoint[],
    },
    {
      dataIndex: 'title',
      key: 'title',
      title: t('fields.title', { ns: 'conveyances'}),
      render: (value: string) => <TextField value={value} />,
      ...getColumnSearchProps('title', 'text'),
      filterIcon: () => (
        <SearchOutlined style={{ color: '#bfbfbf' }} />
      ),
      defaultFilteredValue: getDefaultFilter('title', filters),
    },
    {
      dataIndex: 'owner_name',
      key: 'owner_name',
      title: t('fields.owner_name', { ns: 'conveyances'}),
      render: (value: string) => <TextField value={value || '-'} />,
      ...getColumnSearchProps('owner_name', 'text'),
      filterIcon: () => (
        <SearchOutlined style={{ color: '#bfbfbf' }} />
      ),
      defaultFilteredValue: getDefaultFilter('owner_name', filters),
    },
    {
      dataIndex: 'grant_to_name',
      key: 'grant_to_name',
      title: t('fields.grant_to_name',{ ns: 'conveyances'}),
      render: (value: string) => <TextField value={value || '-'} />,
      ...getColumnSearchProps('grant_to_name', 'text'),
      filterIcon: () => (
        <SearchOutlined style={{ color: '#bfbfbf' }} />
      ),
      defaultFilteredValue: getDefaultFilter('grant_to_name', filters),
    },
    {
      dataIndex: 'status',
      key: 'status',
      title: t('fields.status', { ns: 'conveyances'}),
      render: (value: ConveyanceStatus) => <TextField value={t(`conveyances.status.${value}`, value)} />,
      filters: statusFilters,
      filterMultiple: true,
      onFilter: (value: unknown, record: Conveyance) =>
        Array.isArray(value)
          ? value.includes(record.status)
          : record.status === value,
      defaultFilteredValue: getDefaultFilter('status', filters, 'in') || [],
    },
    {
      dataIndex: 'type',
      key: 'type',
      title: t('fields.type', { ns: 'conveyances'}),
      render: (value: ConveyanceType) => <TextField value={t(`conveyances.type.${value}`, value)} />,
      filters: typeFilters,
      filterMultiple: true,
      onFilter: (value: unknown, record: Conveyance) =>
        Array.isArray(value)
          ? value.includes(record.type)
          : record.type === value,
      defaultFilteredValue: getDefaultFilter('type', filters, 'in') || [],
    },
    {
      dataIndex: 'amount',
      key: 'amount',
      title: t('fields.amount', { ns: 'conveyances'}),
      render: (value: number) => (
        <TextField value={`${value.toLocaleString()} CFA`} />
      ),
    },
    {
      dataIndex: 'start',
      key: 'start',
      title: t('fields.start', { ns: 'conveyances'}),
      render: (value: Date) => <DateField value={value} format="DD/MM/YYYY" />,
    },
    {
      dataIndex: 'attachmentPaths',
      key: 'attachmentPaths',
      title: t('fields.attachment_file', { ns: 'attachments'}),
      render: (paths: string[]) => (
        <Space direction="vertical">
          {paths.length > 0 ? (
            paths.map((path, index) => (
              <a key={index} href={path} target="_blank" rel="noopener noreferrer">
                {path.split('/').pop()}
              </a>
            ))
          ) : (
            <TextField value="-" />
          )}
        </Space>
      ),
    },
    {
      title: t('fields.actions', { ns: 'conveyances'}),
      dataIndex: 'actions',
      key: 'actions',
      fixed: 'right' as const,
      width: 80,
      render: (_: any, record: Conveyance) => (
        <Space size="middle" className="action-buttons">
          <EditButton
            hideText
            size="small"
            recordItemId={record.id}
            icon={<EditOutlined />}
            title={t('buttons.edit', { ns: 'conveyances'})}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <style>{`
        .ownership-list-container {
          padding: 24px;
          background: linear-gradient(135deg, #f0f6ff 0%, #ffffff 100%);
          min-height: 100vh;
          border-radius: 12px;
        }
        .header-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        .create-button-container .ant-btn {
          display: flex;
          align-items: center;
          padding: 8px 16px;
          font-size: 14px;
          font-weight: 600;
          color: #fff;
          background: linear-gradient(135deg, #2f54eb 0%, #1a91ff 100%);
          border: none;
          border-radius: 8px;
          box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
          transition: all 0.3s ease;
        }
        .create-button-container .ant-btn:hover {
          background: linear-gradient(135deg, #1a91ff 0%, #2f54eb 100%);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
        }
        .create-button-container .anticon {
          margin-right: 6px;
          font-size: 16px;
        }
        .reset-button .ant-btn {
          margin-left: 8px;
          border-radius: 6px;
          transition: all 0.3s ease;
          background: #fff;
          border-color: #ff4d4f;
          color: #ff4d4f;
        }
        .reset-button .ant-btn:hover {
          background: #fff1f0;
          color: #cf1322;
          border-color: #cf1322;
        }
        .user-list-table {
          background: #ffffff;
          border-radius: 12px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.1);
          padding: 4px;
          overflow-x: auto;
          font-family: "Segoe UI", Arial, sans-serif;
        }
        .user-list-table .ant-table {
          min-width: 150px;
          border: none;
        }
        .user-list-table .ant-table-thead > tr > th {
          background-color: #1890ff;
          color: #ffffff;
          font-weight: 600;
          border-bottom: none;
          padding: 12px 16px;
        }
        .user-list-table .ant-table-tbody > tr:hover {
          background-color: #f5f7fa;
        }
        .user-list-table .ant-table-tbody > tr > td {
          padding: 12px 16px;
          color: #333;
          font-weight: 500;
        }
        .action-buttons > * {
          border-radius: 50%;
          width: 36px;
          height: 36px;
          display: flex;
          justify-content: center;
          align-items: center;
          transition: background-color 0.3s ease;
          color: #555;
        }
        .action-buttons > *:hover {
          background-color: #e6f7ff;
          color: #1890ff;
        }
        /* Garantir la visibilité de l'icône de recherche */
        .ant-table-filter-trigger {
          display: inline-flex !important;
          visibility: visible !important;
          opacity: 1 !important;
          color: #bfbfbf !important;
        }
        @media (max-width: 768px) {
          .user-list-table .ant-table {
            min-width: 100%;
            font-size: 14px;
          }
          .action-buttons > * {
            width: 32px;
            height: 32px;
          }
        }
        @media (max-width: 480px) {
          .user-list-table .ant-table {
            font-size: 13px;
          }
          .action-buttons > * {
            width: 28px;
            height: 28px;
          }
        }
      `}</style>
      <div className="ownership-list-container">
        <div className="header-container">
          <div className="create-button-container">
            <CreateButton icon={<PlusOutlined />}>
              {t('buttons.create', {
                ns: 'conveyances',
                // defaultValue: 'Créer une conveyance',
              })}
            </CreateButton>
          </div>
          <div className="view-mode-buttons">
            <Button
              icon={<TableOutlined />}
              disabled
            >
              {t('buttons.tableView', {
                ns: 'conveyances',
                // defaultValue: 'Tableau',
              })}
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleResetFilters}
              className="reset-button"
            >
              {t('buttons.resetFilters', {
                ns: 'conveyances',
                defaultValue: 'Réinitialiser',
              })}
            </Button>
          </div>
        </div>
        <Table
          dataSource={filteredDataSource}
          columns={columns}
          rowKey="id"
          className="user-list-table"
          pagination={{
            pageSize,
            current: currentPage,
            total: tableQueryResult?.data?.total,
            onChange: (page) => setCurrentPage(page),
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
          }}
          scroll={{ x: true }}
          onChange={(pagination, tableFilters, sorter) => {
            const newFilters: LogicalFilter[] = Object.entries(tableFilters)
              .reduce<LogicalFilter[]>((acc, [field, value]) => {
                if (Array.isArray(value) && value.length > 0 && value[0] != null) {
                  acc.push({
                    field,
                    operator: field === 'status' || field === 'type' ? 'in' : 'contains',
                    value: field === 'status' || field === 'type' ? value : String(value[0]),
                  });
                }
                return acc;
              }, []);
            setFilters(newFilters, 'replace');
          }}
        />
      </div>
    </>
  );
};
